import React, { useEffect, useState } from 'react';
import CommandPanelImage from 'components/commandpanel/CommandPanelImage';
import {
  GetDisplayMode,
  history,
  isCtrlKeyPressed,
  isNullOrUndefined,
} from 'utils/Global';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { selectNavigatorAvailableImagesKeys } from 'redux/selectors/StateSelectors';
import { getNavigatorCommandPanelImageIcons } from 'utils/Navigator/NavigatorUtils';
import {
  useNavigatorDrops,
  usePlateDrawaingIdentifiers,
  useUpdateCompositeImage,
} from './hooks';
import { getNavigatorImageKeysAsString, getNavFilteredScore } from './utils';
import { NavigatorProps } from './NavigatorThumbnail';
import {
  useAllDropImageParameters,
  useScoresForWells,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import { DropImageParameters, DropUrlParameters } from 'components/Experiment/constants';
import { setWellImagingSettingDetails } from 'redux/reducers/experimentDataReducer';
import { updateDropPath } from 'utils/ExperimentUtils';

const NavigatorDrops = (props: NavigatorProps) => {
  const { setMoveToCanvas, wellNavigationDetails, dropImageParameters } = props;
  const dispatch = useRMDispatch();
  const navAvailableImages = useRMSelector(selectNavigatorAvailableImagesKeys);
  const allWellImagingSettingDetails = useAllDropImageParameters();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { cpId, inspection, regionName, dropNumber } = dropImageParameters || {};
  const selectDrawingIdentifiers = usePlateDrawaingIdentifiers();
  const plateDrawings = selectDrawingIdentifiers(plateID);
  const disabledImageRegions = plateDrawings?.plateDropRegions;
  const displayMode = GetDisplayMode();

  const imageBatchId = inspection?.groupedInspections?.find(
    (grp) => grp.plateId === plateID,
  )?.imageBatchId;
  const [navigatorDrops, setNavigatorDrops] = useState([]);
  const updateCompositeImage = useUpdateCompositeImage();
  const currentWellScores = useScoresForWells([
    {
      wellNumber: wellNum,
      plateId: plateID,
      dropNumber: dropNumber,
    },
  ]);

  useEffect(() => {
    const compositeKey = getNavigatorImageKeysAsString(
      wellNavigationDetails,
      dropImageParameters,
    );
    if (navAvailableImages.includes(compositeKey)) {
      setNavigatorDrops(useNavigatorDrops(compositeKey));
    }
  }, [navAvailableImages, wellNavigationDetails, dropImageParameters]);

  useEffect(() => {
    updateCompositeImage(wellNavigationDetails, dropImageParameters);
  }, [displayMode]);

  function changeDropNumber(dpNumber, ctrlKey) {
    const dropNumber = parseInt(dpNumber, 10);
    if (!ctrlKey) {
      updateDropPath(history.location.pathname, [
        { index: DropUrlParameters.DROPNUMBER, value: dropNumber },
      ]);
    }

    const navigationDetails: DropImageParameters = {
      ...dropImageParameters,
      dropNumber,
    };

    updateCompositeImage(wellNavigationDetails, navigationDetails);

    const selectedNav = allWellImagingSettingDetails.find(
      (w) =>
        w.dropNumber === dropNumber &&
        w.cpId === navigationDetails.cpId &&
        w.inspection === navigationDetails.inspection &&
        w.regionName === navigationDetails.regionName,
    );

    if (
      allWellImagingSettingDetails.length === 1 &&
      !isNullOrUndefined(selectedNav) &&
      ctrlKey
    ) {
      return;
    }
    const filteredNavDetails = allWellImagingSettingDetails.filter(
      (w) =>
        w.dropNumber !== navigationDetails.dropNumber &&
        w.cpId === navigationDetails.cpId &&
        w.inspection.groupedInspections.find((i) => i.imageBatchId === imageBatchId) &&
        w.regionName === navigationDetails.regionName,
    );

    if (!isNullOrUndefined(selectedNav) && filteredNavDetails.length && ctrlKey) {
      dispatch(setWellImagingSettingDetails([...filteredNavDetails]));
      return;
    }
    if (ctrlKey) {
      if (allWellImagingSettingDetails.length < 4) {
        dispatch(
          setWellImagingSettingDetails([...filteredNavDetails, navigationDetails]),
        );
      }
      setMoveToCanvas(true);
    } else {
      dispatch(setWellImagingSettingDetails([navigationDetails]));
    }
  }

  return (
    <div id="navigatordrop-container" className="tabContainer">
      {navigatorDrops
        ? navigatorDrops.map((drop) => (
            <CommandPanelImage
              image={drop.image}
              inspectionName={drop.name}
              imageName={drop.imageName}
              inspectionDateTime={drop.dateImaged}
              key={drop.ImageIndex}
              active={
                allWellImagingSettingDetails.length > 1
                  ? allWellImagingSettingDetails.some(
                      (details) => details.dropNumber === drop.dropNumber,
                    )
                  : selectedDropImageParameters.dropNumber === drop.dropNumber
              }
              onClick={(e) => changeDropNumber(drop.dropNumber, isCtrlKeyPressed(e))}
              iconImages={getNavigatorCommandPanelImageIcons(
                plateDrawings,
                disabledImageRegions,
                wellNum,
                drop.dropNumber,
                regionName,
                cpId,
                imageBatchId,
              )}
              currentWellScores={getNavFilteredScore(
                currentWellScores,
                wellNum,
                drop.dropNumber,
                imageBatchId,
              )}
            />
          ))
        : null}
    </div>
  );
};

export default NavigatorDrops;
